import { computed, onMounted, reactive, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router/composables'
import { deleteBookshelfBooks, deleteReadHistory, fetchBookshelfList, fetchReadHistoryList } from '../services/historyApi'
import type { HistoryBook, HistoryBookRowView, HistoryPagedState, HistoryTab, LibraryBook, ReadHistoryBook } from '../types'
import type { HistoryCommonConfig } from '../configs/types'
import { TRACK_CHANNEL_NAME_VALUE } from '@/constants'
import { t } from '@/i18n/utils'
import { useReadStore, useToastStore, useTrackValueStore } from '@/stores'
import { READER_ROUTE_NAME } from '@/utils/readerRouteName'

const PAGE_SIZE = 20

function createPagedState<T>(): HistoryPagedState<T> {
  return reactive({
    items: [],
    page: 1,
    ending: false,
    fetching: false,
    error: undefined,
  }) as HistoryPagedState<T>
}

function joinMeta(parts: Array<string | undefined>) {
  return parts.filter(Boolean).join('\u00A0\u00A0·\u00A0\u00A0')
}

export function useHistoryPage(config: HistoryCommonConfig, coverPlaceholder: string) {
  const route = useRoute()
  const router = useRouter()
  const { $toast } = useToastStore()
  const readStore = useReadStore()

  const from = computed(() => route.query.from as string | undefined)
  const activeTab = ref<HistoryTab>(route.query.tab === 'history' ? 'history' : 'library')

  const editMode = ref(false)
  const selectedIds = ref<Set<string>>(new Set())
  const deleteConfirmVisible = ref(false)

  const library = createPagedState<LibraryBook>()
  const readHistory = createPagedState<ReadHistoryBook>()

  const currentItems = computed<HistoryBook[]>(() =>
    activeTab.value === 'library' ? library.items : readHistory.items,
  )

  const isAllSelected = computed(() => {
    return currentItems.value.length > 0 && selectedIds.value.size === currentItems.value.length
  })

  const showTabbar = computed(() => !from.value && !editMode.value)

  function resetLibrary() {
    library.items = []
    library.page = 1
    library.ending = false
    library.error = undefined
  }

  function resetHistory() {
    readHistory.items = []
    readHistory.page = 1
    readHistory.ending = false
    readHistory.error = undefined
  }

  async function fetchLibrary() {
    library.fetching = true
    try {
      const { data } = await fetchBookshelfList(library.page, PAGE_SIZE)
      const records = data.records || []
      const next = [...library.items, ...records]
      next.sort((a, b) => {
        const ta = a.lastReadTime ? new Date(a.lastReadTime).getTime() : 0
        const tb = b.lastReadTime ? new Date(b.lastReadTime).getTime() : 0
        return tb - ta
      })
      library.items = next
      if (library.page >= (data.pages || 0))
        library.ending = true
      else
        library.page += 1
    }
    catch (e) {
      library.error = e
    }
    finally {
      library.fetching = false
    }
  }

  async function fetchHistory() {
    readHistory.fetching = true
    try {
      const { data } = await fetchReadHistoryList(readHistory.page, PAGE_SIZE)
      readHistory.items = [...readHistory.items, ...(data.records || [])]
      if (readHistory.page >= (data.pages || 0))
        readHistory.ending = true
      else
        readHistory.page += 1
    }
    catch (e) {
      readHistory.error = e
    }
    finally {
      readHistory.fetching = false
    }
  }

  async function loadCurrent() {
    if (activeTab.value === 'library') {
      if (library.fetching || library.ending || library.error)
        return
      await fetchLibrary()
      return
    }

    if (readHistory.fetching || readHistory.ending || readHistory.error)
      return
    await fetchHistory()
  }

  function switchTab(tab: HistoryTab) {
    if (activeTab.value === tab)
      return
    activeTab.value = tab
  }

  function enterEdit() {
    editMode.value = true
    selectedIds.value = new Set()
  }

  function exitEdit() {
    editMode.value = false
    selectedIds.value = new Set()
  }

  function toggleSelect(id: string) {
    const ids = new Set(selectedIds.value)
    if (ids.has(id))
      ids.delete(id)
    else
      ids.add(id)
    selectedIds.value = ids
  }

  function toggleSelectAll() {
    if (isAllSelected.value)
      selectedIds.value = new Set()
    else
      selectedIds.value = new Set(currentItems.value.map(item => item.contentId))
  }

  function openDeleteConfirm() {
    if (selectedIds.value.size === 0)
      return
    deleteConfirmVisible.value = true
  }

  function closeDeleteConfirm() {
    deleteConfirmVisible.value = false
  }

  async function confirmDelete() {
    const ids = Array.from(selectedIds.value)
    if (ids.length === 0) {
      closeDeleteConfirm()
      return
    }

    if (activeTab.value === 'library') {
      await deleteBookshelfBooks(ids)
      closeDeleteConfirm()
      exitEdit()
      resetLibrary()
      await fetchLibrary()
      return
    }

    await deleteReadHistory(ids)
    closeDeleteConfirm()
    exitEdit()
    resetHistory()
    await fetchHistory()
  }

  function gotoBookOrReader(item: HistoryBook) {
    if ((item as LibraryBook).offlineFlag)
      return $toast(config.texts.bookRemoved)

    const trackValueStore = useTrackValueStore()
    // Legacy tracking uses the literal "library" for bookshelf entries.
    const chlName = activeTab.value === 'library'
      ? ('library' as unknown as number)
      : TRACK_CHANNEL_NAME_VALUE.READ_HISTORY
    trackValueStore.set({
      bookId: item.contentId,
      firstOrigin: 4,
      originName: 4,
      chlName,
    })

    if (item.contentChapterId) {
      readStore.update({ contentId: item.contentId, chapterId: item.contentChapterId })
      router.push({
        name: READER_ROUTE_NAME,
        params: { contentId: item.contentId, chapterId: item.contentChapterId },
        query: activeTab.value === 'library' ? { from: 'library' } : {},
      })
      return
    }

    router.push({ name: 'book', params: { contentId: item.contentId } })
  }

  function onItemClick(item: HistoryBook) {
    if (editMode.value) {
      toggleSelect(item.contentId)
      return
    }
    gotoBookOrReader(item)
  }

  function onCoverError(e: Event) {
    const img = e.target as HTMLImageElement
    if (img && img.src !== coverPlaceholder)
      img.src = coverPlaceholder
  }

  function authorOf(item: HistoryBook): string {
    const info: any = (item as any).authorInfo
    if (!info)
      return ''
    return info.author || info.key?.author || ''
  }

  function progressOf(item: LibraryBook): string {
    const p = typeof item.readProgress === 'number' ? item.readProgress : 0
    return t('ReadingRate', { 0: `${p}%` })
  }

  function statusOf(item: HistoryBook): string {
    return (item as any).finishStatus === 1 ? config.texts.completed : config.texts.ongoing
  }

  function chapterTextOf(item: HistoryBook): string {
    const num = (item as any).chapterNum
    return num ? `${num} ${t('Chapters')}` : ''
  }

  function toBookRowView(item: HistoryBook, tab: HistoryTab): HistoryBookRowView {
    if (tab === 'library') {
      return {
        id: item.contentId,
        title: item.contentName,
        coverUrl: item.contentCoverUrl,
        metaLines: [
          joinMeta([progressOf(item as LibraryBook), authorOf(item)]),
          joinMeta([statusOf(item), chapterTextOf(item)]),
        ],
      }
    }

    return {
      id: item.contentId,
      title: item.contentName,
      coverUrl: item.contentCoverUrl,
      metaLines: [
        joinMeta([statusOf(item), chapterTextOf(item)]),
        item.contentChapterName ? `${config.texts.lastRead}：${item.contentChapterName}` : '',
      ].filter(Boolean),
    }
  }

  onMounted(() => {
    if (activeTab.value === 'library')
      fetchLibrary()
    else
      fetchHistory()
  })

  watch(activeTab, async (next) => {
    exitEdit()
    if (next === 'library' && library.items.length === 0 && !library.fetching)
      await fetchLibrary()
    if (next === 'history' && readHistory.items.length === 0 && !readHistory.fetching)
      await fetchHistory()
  })

  return {
    from,
    activeTab,
    editMode,
    selectedIds,
    deleteConfirmVisible,
    library,
    readHistory,
    isAllSelected,
    showTabbar,
    loadCurrent,
    switchTab,
    enterEdit,
    exitEdit,
    toggleSelectAll,
    openDeleteConfirm,
    closeDeleteConfirm,
    confirmDelete,
    onItemClick,
    onCoverError,
    toBookRowView,
  }
}
